import { resolveId } from '@medplum/core'
import type { Bundle } from '@medplum/fhirtypes'
import type {
  ExtendedResource,
  ExtendedResourceConstructor,
} from '@seen/core/ExtendedResource'
import { invariant } from '@seen/core/invariant'
import {
  getSearchKey,
  normalizeQuery,
  type QueryTypes,
} from '@seen/core/search'
import type { UseQueryResult } from '@tanstack/react-query'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

type SearchType = 'text' | 'hybrid'

export interface FullTextSearchRequest {
  query: QueryTypes
  fullTextQuery: string
  projectId: string
  searchType: SearchType
}

export interface UseFullTextSearchOptions {
  searchType?: SearchType
  fullTextQuery: string
  enabled?: boolean
}

export function useFullTextSearch<T extends ExtendedResource>(
  resourceClass: ExtendedResourceConstructor<T>,
  query: QueryTypes,
  options: UseFullTextSearchOptions
): UseQueryResult<Bundle> {
  const medplum = useMedplum()
  const { seenApi } = useSeenApi()

  const login = medplum.getActiveLogin()
  invariant(login?.project, 'No login found')
  const projectId = resolveId(login.project)
  invariant(projectId, 'No project ID found')

  const {
    searchType = 'text',
    fullTextQuery,
    enabled: enabledOpt = true,
  } = options

  // Only enable query if we have all required parameters
  const enabled = !!(query && projectId && fullTextQuery && enabledOpt)

  const parameters = normalizeQuery({ _count: 1000, ...query })
  const resourceType = resourceClass.RESOURCE_TYPE
  const medplumSearchKey = getSearchKey(medplum, resourceType, parameters)

  const searchKey = `${projectId}-${searchType}-${medplumSearchKey}-${fullTextQuery}`

  const queryResult = useQuery({
    // oxlint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: cacheKeys.fullTextSearch(resourceType, searchKey),
    queryFn: async (): Promise<Bundle> => {
      const response = await seenApi.post(`/search/${resourceType}`, {
        query: parameters,
        fullTextQuery,
        projectId,
        searchType,
      } satisfies FullTextSearchRequest)

      const bundle = response.data.body as Bundle
      return bundle
    },
    enabled,
  })

  useErrorToast(
    queryResult.error &&
      `Error performing full text search on ${resourceType}: ${queryResult.error.message ?? 'Unknown error'}`
  )

  return queryResult
}
